import { z } from "zod";
import { IdentifiedOrDeletedAuthor } from "./author";
import { COMPASS_EVENT_KINDS } from "../utils/personalEvents";

// PRTL-739: meeting-backed events (admin-managed Compass Calls) render
// distinctly from user-created CompassEvents on /private-events + the
// navigator calendar. The four fields below are resolved server-side per
// row so UI can render the badge/join CTA without an extra round-trip.
//   - isMeeting:          true when the row's CompassEvent.meetingId is set
//   - instanceShortCode:  CompassEvent.shortCode — used to link into /meetings/{code}
//   - seriesShortCode:    Meeting.shortCode — used to link into /meetings/series/{code}
//   - effectiveZoomUrl:   CompassEvent.zoomUrl ?? Meeting.zoomUrl (fallback link)
export const CompassEvent = z.object({
    id: z.number(),
    // PRTL-1456: null on a Compass event — entered by staff in admin, on every
    // user's calendar, read-only to them. Branch on `isCompassEvent`.
    userId: z.number().nullable(),
    isCompassEvent: z.boolean(),
    // PRTL-850: on the viewer's calendar because they're an additional attendee. Read-only
    // to them (no Edit/Delete); only the primary attendee (`userId`) edits a personal event.
    isAttendeeOnly: z.boolean(),
    // PRTL-850: everyone on it (primary attendee first) as author chips, when it has
    // additional attendees; empty otherwise. Calendar rows only (favorites omit it).
    attendees: z.array(IdentifiedOrDeletedAuthor).optional(),
    title: z.string(),
    description: z.string().nullable(),
    createdAt: z.date(),
    updatedAt: z.date(),
    start: z.date(),
    end: z.date().nullable(),
    isMeeting: z.boolean(),
    instanceShortCode: z.string().nullable(),
    seriesShortCode: z.string().nullable(),
    effectiveZoomUrl: z.string().nullable(),
    // PRTL-705: reminder types. All-day entries store `start` at UTC midnight of
    // their date (see utils/personalEvents); clients expand yearly repeats.
    kind: z.enum(COMPASS_EVENT_KINDS),
    isAllDay: z.boolean(),
    repeatYearly: z.boolean(),
    leadTimeDays: z.number().int().nullable(),
    // PRTL-705: set on rows from the range queries, where a yearly entry
    // appears once per occurrence with the same `id`: `<id>:<year>`. Use it
    // as the list/grid key; edits and deletes still go to `id` (the series).
    occurrenceKey: z.string().optional(),
    /** The entry's own stored dates when `start`/`end` are a later occurrence's. */
    seriesStart: z.date().optional(),
    seriesEnd: z.date().nullable().optional(),
});
